import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminApi } from "@/api";
import LicencesTab from "@/components/LicencesTab";
import ScriptsTab from "@/components/ScriptsTab";
import LogsTab from "@/components/LogsTab";

export default function AdminPanel() {
  const [authed, setAuthed] = useState(null); // null = vérification en cours
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("lic");

  const loadStats = useCallback(async () => {
    try {
      const r = await adminApi().get("/admin/stats");
      setStats(r.data);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403) setAuthed(false);
    }
  }, []);

  useEffect(() => {
    adminApi()
      .get("/admin/auth/me")
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (authed) loadStats();
  }, [authed, loadStats]);

  const login = async () => {
    if (!email.trim() || !password) return;
    setChecking(true);
    try {
      await adminApi().post("/admin/auth/login", { email: email.trim(), password });
      setPassword("");
      setAuthed(true);
    } catch (e) {
      toast.error(
        e?.response?.status === 429
          ? "Trop de tentatives — réessaie dans 15 minutes."
          : "Identifiants invalides"
      );
    } finally {
      setChecking(false);
    }
  };

  const logout = async () => {
    try { await adminApi().post("/admin/auth/logout"); } catch { /* ignore */ }
    setAuthed(false);
    setStats(null);
  };

  if (authed === null) return null;

  if (!authed) {
    return (
      <div className="card keygate" data-testid="admin-login-gate">
        <h2>Connexion admin</h2>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label className="lbl" style={{ display: "block", marginBottom: 6 }}>Email</label>
          <input
            type="email"
            placeholder="admin@mail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
          />
        </div>
        <div className="fld" style={{ marginBottom: 14 }}>
          <label className="lbl" style={{ display: "block", marginBottom: 6 }}>Mot de passe</label>
          <input
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            data-testid="admin-password-input"
          />
        </div>
        <button
          className="btn btn-p"
          style={{ width: "100%" }}
          onClick={login}
          disabled={checking}
          data-testid="admin-login-submit"
        >
          Se connecter
        </button>
      </div>
    );
  }

  return (
    <section data-testid="admin-panel-view">
      <div className="statrow">
        <div className="stat" data-testid="stat-active-licenses">
          <div className="lbl">Licences actives</div>
          <div className="n">{stats ? stats.active_licenses : "…"}</div>
        </div>
        <div className="stat" data-testid="stat-optimizations">
          <div className="lbl">Optimisations (30 j)</div>
          <div className="n">{stats ? stats.optimizations_30d : "…"}</div>
        </div>
        <div className="stat" data-testid="stat-profiles">
          <div className="lbl">Profils</div>
          <div className="n">{stats ? stats.profiles : "…"}</div>
        </div>
      </div>

      <div className="tabs">
        <button className={tab === "lic" ? "on" : ""} onClick={() => setTab("lic")} data-testid="tab-licences">Licences</button>
        <button className={tab === "scripts" ? "on" : ""} onClick={() => setTab("scripts")} data-testid="tab-scripts">Scripts</button>
        <button className={tab === "logs" ? "on" : ""} onClick={() => setTab("logs")} data-testid="tab-logs">Logs</button>
        <span style={{ flex: 1 }} />
        <button
          className="btn btn-sm"
          style={{ alignSelf: "center", marginBottom: 6 }}
          onClick={logout}
          data-testid="admin-logout-btn"
        >
          Déconnexion
        </button>
      </div>

      {tab === "lic" && <LicencesTab onChanged={loadStats} />}
      {tab === "scripts" && <ScriptsTab onChanged={loadStats} />}
      {tab === "logs" && <LogsTab />}
    </section>
  );
}
